import React from "react";
import * as Sentry from "@sentry/react";
import InhouseLogo from "src/images/logos/inhouse-logo-large.png";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS } from "src/utils/EventConstants";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    try {
      trackAnalytics(EVENT_NAMES.VIEWED_ERROR_BOUNDARY, {
        [EVENT_PARAMETERS.ERROR_MESSAGE]: error?.message,
        [EVENT_PARAMETERS.PAGE_NAME]: window.location.pathname,
      });
    } catch {
      // The error fallback must render even if analytics itself is broken.
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] p-6">
          <div className="max-w-[480px] w-full bg-white rounded-[24px] p-[40px] flex flex-col items-center text-center shadow-xl">
            <img src={InhouseLogo} alt="Inhouse" className="w-[120px] object-contain mb-8" draggable={false} />
            <h1 className="text-[22px] font-[600] text-[#1B2B48] mb-3">Something went wrong</h1>
            <p className="text-[15px] text-[#353535] mb-8 leading-relaxed">
              An unexpected error occurred on our end. Please reload the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-base btn-secondary w-full text-[16px] font-[500]"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
